import { Checkbox, CheckboxProps } from 'baseui/checkbox';
import { Typography } from 'baseui/themes';
import { CheckboxCheckedSVG, CheckboxIndeterminateSVG } from 'common/assets';
import mediaQuery from 'common/styles/styles.breakpoint';
import typography from 'common/styles/styles.typography';
import * as React from 'react';

import { CheckedBox, IndeterminateBox, UncheckedBox } from './style';
import Text from '../text';

type MediaQueryType = keyof typeof mediaQuery | 'initial';

const checkboxOverrides = {
  Toggle: {
    style: ({ $checked, $theme }) => ({
      backgroundColor: $checked ? $theme.colors.secondaryDark : 'white',
    }),
  },
  ToggleTrack: {
    style: ({ $theme }) => ({
      backgroundColor: $theme.colors.skyLighter,
    }),
  },
  Root: {
    style: {
      width: 'fit-content',
      alignItems: 'center',
    },
  },
  Checkmark: {
    component: ({ $checked, $isIndeterminate, $disabled }) => {
      if ($isIndeterminate)
        return (
          <IndeterminateBox $disabled={$disabled}>
            <CheckboxIndeterminateSVG />
          </IndeterminateBox>
        );
      else if ($checked)
        return (
          <CheckedBox $disabled={$disabled}>
            <CheckboxCheckedSVG />
          </CheckedBox>
        );
      return <UncheckedBox $disabled={$disabled} />;
    },
  },
};

export default function IndeterminateCheckbox({
  indeterminate,
  ref,
  label,
  labelVariant = 'LabelXSmall',
  ...rest
}: {
  indeterminate?: boolean;
  label?: string;
  ref?: any;
  labelVariant?:
    | (keyof Typography | keyof typeof typography)
    | Partial<
        Record<MediaQueryType, keyof Typography | keyof typeof typography>
      >;
} & CheckboxProps) {
  const defaultRef = React.useRef<HTMLInputElement>(null!);

  React.useEffect(() => {
    if (ref) {
      ref.current = defaultRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRef.current, ref]);

  React.useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      defaultRef.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <Checkbox
      {...rest}
      inputRef={defaultRef}
      overrides={checkboxOverrides}
      isIndeterminate={!rest.checked && indeterminate}
    >
      {label && <Text variant={labelVariant}>{label}</Text>}
    </Checkbox>
  );
}
